/**
 * Retrieves all records from the specified IndexedDB object store.
 * If the store does not exist, it will be created.
 *
 * @param {string} dbName - The name of the IndexedDB database.
 * @param {string} storeName - The name of the object store to retrieve records from.
 * @returns {Promise<any[]>} - A promise that resolves with an array of records from the object store.
 */
export function getAllRecordsFromIndexedDB(dbName: string, storeName: string): Promise<any[]> {
  return new Promise((resolve, reject) => {
    const openRequest = indexedDB.open(dbName, 2);

    openRequest.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db = (event.target as IDBOpenDBRequest).result as IDBDatabase;
      if (!db.objectStoreNames.contains(storeName)) {
        console.log(`Creating object store "${storeName}"`);
        db.createObjectStore(storeName, { keyPath: 'id' });
      }
    };

    openRequest.onsuccess = (event: Event) => {
      const db = (event.target as IDBRequest).result as IDBDatabase;
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const getAllRequest = store.getAll();

      getAllRequest.onsuccess = (evt: Event) => {
        const cachedSessions = (evt.target as IDBRequest).result as any[];
        resolve(cachedSessions);
      };

      getAllRequest.onerror = (evt: Event) => {
        console.error('Error getting records:', (evt.target as IDBRequest).error);
        reject((evt.target as IDBRequest).error);
      };
    };

    openRequest.onerror = (event: Event) => {
      console.error('Database error:', (event.target as IDBRequest).error);
      reject((event.target as IDBRequest).error);
    };
  });
}

/**
 * Retrieves a specific record from the IndexedDB object store by its ID.
 *
 * @param {string} dbName - The name of the IndexedDB database.
 * @param {string} storeName - The name of the object store.
 * @param {string | number} id - The ID of the record to retrieve.
 * @returns {Promise<T | undefined>} - A promise that resolves with the record or undefined if not found.
 * @template T - The type of the record in the object store.
 */
export async function getRecordFromIndexedDB<T>(dbName: string, storeName: string, id: string | number): Promise<T | undefined> {
  try {
    // Open the IndexedDB connection
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(dbName, 2); // Version 2 of IndexedDB

      request.onerror = (event: Event) => {
        reject(`Error opening database: ${(event.target as IDBRequest).error}`);
      };

      request.onsuccess = (event: Event) => {
        resolve((event.target as IDBRequest).result as IDBDatabase); // Resolve with the DB instance
      };
    });

    // Start a transaction and get the object store
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);

    // Use the get method to retrieve the record
    const record = await new Promise<T | undefined>((resolve, reject) => {
      const getRequest = store.get(id);

      getRequest.onsuccess = (event: Event) => {
        resolve((event.target as IDBRequest).result as T | undefined); // Resolve with the record
      };

      getRequest.onerror = (event: Event) => {
        reject(`Error retrieving record: ${(event.target as IDBRequest).error}`);
      };
    });

    return record; // Return the fetched record

  } catch (error) {
    console.error(error);
    throw error; // Rethrow the error for further handling if needed
  }
}

/**
 * Deletes a specific record from the IndexedDB object store by its ID.
 *
 * @param {string} dbName - The name of the IndexedDB database.
 * @param {string} storeName - The name of the object store.
 * @param {string | number} id - The ID of the record to delete.
 * @param {Function} callback - A callback function that is called with the result of the operation.
 *   The callback receives an error (if any) as the first argument, and a success message (or null) as the second argument.
 * @returns {void} - This function does not return anything; it calls the callback instead.
 */
export function deleteRecordByIdFromIndexedDB(
  dbName: string, 
  storeName: string, 
  id: string | number, 
  callback: (error: any, message: string | null) => void,
): void {
  const openRequest = indexedDB.open(dbName, 2);

  openRequest.onupgradeneeded = function(event: IDBVersionChangeEvent) {
    const db = (event.target as IDBOpenDBRequest).result as IDBDatabase;
    if (!db.objectStoreNames.contains(storeName)) {
      console.log(`Creating object store ${storeName}`);
      db.createObjectStore(storeName, { keyPath: 'id' });
    }
  };

  openRequest.onsuccess = function(event: Event) {
    const db = (event.target as IDBRequest).result as IDBDatabase;
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const deleteRequest = store.delete(id);

    deleteRequest.onsuccess = function() {
      console.log(`Record with id ${id} deleted successfully`);
      callback(null, `Record with id ${id} deleted successfully`);
    };

    deleteRequest.onerror = function(event: Event) {
      console.error('Error deleting record:', (event.target as IDBRequest).error);
      callback((event.target as IDBRequest).error, null);
    };
  };

  openRequest.onerror = function(event: Event) {
    console.error('Database error:', (event.target as IDBRequest).error);
    callback((event.target as IDBRequest).error, null);
  };
}

/**
 * Opens a connection to the IndexedDB database and object store.
 *
 * @param {string} dbName - The name of the IndexedDB database to open.
 * @param {string} storeName - The name of the object store to open.
 * @returns {Promise<IDBDatabase>} - A promise that resolves with the opened IndexedDB database instance.
 */
export function openDatabase(
  dbName: string,
  storeName: string,
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request: IDBOpenDBRequest = indexedDB.open('AudioRecorderDB', 2);

    request.onsuccess = (event: Event) => {
      const db: IDBDatabase = (event.target as IDBRequest).result;
      resolve(db); // Resolve the promise with the db object
    };

    request.onerror = (event: Event) => {
      console.error('Error opening IndexedDB:', event);
      reject(event); // Reject the promise if there's an error
    };

    request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
      const db: IDBDatabase = (event.target as IDBRequest).result;
      if (!db.objectStoreNames.contains('audioData')) {
        db.createObjectStore('audioData', { keyPath: 'id' });
      }
    };
  });
}

/**
 * Stores a recording cache object into the specified IndexedDB object store.
 *
 * @param {IDBDatabase} dbObject - The IndexedDB database object where the data will be stored.
 * @param {string} storeName - The name of the object store where the recording cache will be stored.
 * @param {RecordingCache} recordingCache - The recording cache object containing the data to be stored.
 * @returns {void} - This function does not return any value.
 */
interface RecordingCache {
  birthTime: string,
  data: Blob,
  id: string | null,
  localSessionId: string | null,
  sessionTitle: string,
  updateAt: object,
}

export function storeRecordingCacheToIndexedDB (dbObject: IDBDatabase, storeName: string, recordingCache: RecordingCache) {
  const transaction: IDBTransaction = dbObject.transaction(storeName, 'readwrite');
  const store: IDBObjectStore = transaction.objectStore(storeName);
  const request: IDBRequest = store.put(recordingCache);

  // Handle errors in the request
  request.onerror = (event: Event) => {
    const target = event.target as IDBRequest; // Type assertion to get the request
    // Check if the error is related to storage exceeding the quota
    if (target.error && target.error.name === 'QuotaExceededError') {
      console.error('Storage limit exceeded!');
    } else if (target.error) {
      console.error('Error:', target.error);
    }
  };
}
